import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import dayjs from 'dayjs';
import {
  batchApprovalApi,
  PendingItem,
  BatchItem,
  BatchResponse,
  ModuleInfo,
  BatchApprovalFilter,
} from '../../../services/batchApprovalApi';
import { buildRowKey, parseRowKey } from './useBatchApproval';

// ============================================================
// Types
// ============================================================

export interface UseBatchWriteoffReturn {
  // State
  loading: boolean;
  operating: boolean;
  items: PendingItem[];
  total: number;
  modules: ModuleInfo[];
  selectedKeys: string[];
  filter: BatchApprovalFilter;
  resultData: BatchResponse | null;
  resultVisible: boolean;

  // Actions
  setFilter: (f: Partial<BatchApprovalFilter>) => void;
  setSelectedKeys: (keys: string[]) => void;
  fetchApproved: () => Promise<void>;
  batchWriteoff: (comment: string) => Promise<void>;
  toggleSelect: (key: string) => void;
  selectAll: () => void;
  clearSelection: () => void;
  closeResult: () => void;
}

// Default: last 30 days
function defaultDateFrom(): string {
  return dayjs().subtract(30, 'day').format('YYYY-MM-DD');
}

function defaultDateTo(): string {
  return dayjs().format('YYYY-MM-DD');
}

// ============================================================
// Hook
// ============================================================

export function useBatchWriteoff(): UseBatchWriteoffReturn {
  const [loading, setLoading] = useState(false);
  const [operating, setOperating] = useState(false);
  const [items, setItems] = useState<PendingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [modules, setModules] = useState<ModuleInfo[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [resultData, setResultData] = useState<BatchResponse | null>(null);
  const [resultVisible, setResultVisible] = useState(false);
  const [filter, setFilterState] = useState<BatchApprovalFilter>({
    modId: '',
    dateFrom: defaultDateFrom(),
    dateTo: defaultDateTo(),
    page: 1,
    size: 50,
  });

  // Load modules on mount
  useEffect(() => {
    batchApprovalApi.getModules().then((all) => setModules(all.filter((m) => !m.approvalOnly))).catch(() => {
      // silently fail
    });
  }, []);

  const setFilter = useCallback((partial: Partial<BatchApprovalFilter>) => {
    setFilterState((prev) => ({ ...prev, ...partial, page: partial.page ?? 1 }));
  }, []);

  const fetchApproved = useCallback(async () => {
    setLoading(true);
    setSelectedKeys([]);
    try {
      const resp = await batchApprovalApi.getApproved(filter);
      setItems(resp.data || []);
      setTotal(resp.total || 0);
    } catch (err: unknown) {
      const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '查詢失敗');
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const buildSelectedItems = useCallback((): BatchItem[] => {
    return selectedKeys.map(parseRowKey);
  }, [selectedKeys]);

  const batchWriteoff = useCallback(async (comment: string) => {
    const batchItems = buildSelectedItems();
    if (batchItems.length === 0) {
      message.warning('請至少勾選一筆文件');
      return;
    }
    setOperating(true);
    try {
      const resp = await batchApprovalApi.batchWriteoff(batchItems, comment);
      setResultData(resp);
      setResultVisible(true);
      await fetchApproved();
    } catch (err: unknown) {
      const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '沖銷失敗');
      message.error(msg);
    } finally {
      setOperating(false);
    }
  }, [buildSelectedItems, fetchApproved]);

  const toggleSelect = useCallback((key: string) => {
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }, []);

  const selectAll = useCallback(() => {
    setSelectedKeys(items.map(buildRowKey));
  }, [items]);

  const clearSelection = useCallback(() => {
    setSelectedKeys([]);
  }, []);

  const closeResult = useCallback(() => {
    setResultVisible(false);
    setResultData(null);
  }, []);

  return {
    loading,
    operating,
    items,
    total,
    modules,
    selectedKeys,
    filter,
    resultData,
    resultVisible,
    setFilter,
    setSelectedKeys,
    fetchApproved,
    batchWriteoff,
    toggleSelect,
    selectAll,
    clearSelection,
    closeResult,
  };
}
